/**
 * Reports.
 *
 * One screen for all seven, because the API gives them all the same shape: columns, then rows,
 * with a type on each column. That is what lets this render money as money and a date as a date
 * without knowing which report it is looking at — and what makes an eighth report a query on the
 * server rather than a page here.
 *
 * Sorting and the totals row are client-side on purpose: the whole report is already in hand
 * (these are tens to hundreds of rows, not millions), so a sort should not be a round trip.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import { ErrorNote, Money, PageHeader } from '@/components/bits';
import {
  Alert,
  Badge,
  Card,
  DownloadButton,
  EmptyState,
  Select,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { formatDate, formatInstant } from '@/lib/dates';
import type { ReportColumn, ReportData } from '@/lib/types';

const REPORTS: Array<{ name: string; label: string; blurb: string }> = [
  {
    name: 'utilisation',
    label: 'Utilisation',
    blurb: 'What each department has, has reserved, has spent, and stands to forfeit.',
  },
  {
    name: 'events',
    label: 'Event register',
    blurb: 'Every event with its cost, attendance and settlement.',
  },
  {
    name: 'exceptions',
    label: 'Exceptions',
    blurb: 'Every justification and every decision, side by side.',
  },
  {
    name: 'aging',
    label: 'Aging',
    blurb: 'What is waiting, on whom, and for how long.',
  },
  {
    name: 'unpaid',
    label: 'Unpaid',
    blurb: 'Approved and settled, waiting on the money to move.',
  },
  {
    name: 'unarchived',
    label: 'Unarchived evidence',
    blurb: 'Should always be empty — verification requires an archive link.',
  },
  {
    name: 'cross-department',
    label: 'Cross-department',
    blurb: 'Guests from other teams, and what they brought.',
  },
];

export function Reports() {
  const session = useSession();
  const [name, setName] = useState('utilisation');
  const [sort, setSort] = useState<{ key: string; direction: 1 | -1 } | null>(null);

  const params = { year: session.year };
  const report = useQuery({
    queryKey: ['report', name, session.year],
    queryFn: () => api.report(name, params),
  });

  const chosen = REPORTS.find((entry) => entry.name === name);
  const data = report.data;

  const rows = useMemo(() => {
    if (!data) return [];
    if (!sort) return data.rows;
    const column = data.columns.find((each) => each.key === sort.key);
    return [...data.rows].sort(
      (a, b) => compare(a[sort.key], b[sort.key], column) * sort.direction,
    );
  }, [data, sort]);

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle={`${session.year} · ${chosen?.blurb ?? ''}`}
        actions={
          data ? (
            <>
              {/*
                Real links, not fetches: the browser downloads the file and the
                Content-Disposition the server already sends decides its name.
              */}
              <DownloadButton href={api.reportExportUrl(name, params, 'xlsx')} variant="primary">
                Excel
              </DownloadButton>
              <DownloadButton href={api.reportExportUrl(name, params, 'csv')}>CSV</DownloadButton>
            </>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select
          aria-label="Report"
          className="w-64"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setSort(null);
          }}
        >
          {REPORTS.map((entry) => (
            <option key={entry.name} value={entry.name}>
              {entry.label}
            </option>
          ))}
        </Select>
        {data ? (
          <span className="text-muted-foreground text-xs">
            {data.rows.length} {data.rows.length === 1 ? 'row' : 'rows'} · generated{' '}
            {formatInstant(data.generated_at)}
          </span>
        ) : null}
      </div>

      {report.error ? <ErrorNote error={report.error} /> : null}

      <Card title={data?.title ?? chosen?.label ?? 'Report'}>
        {data?.note ? (
          <Alert tone="neutral" className="mb-4">
            {data.note}
          </Alert>
        ) : null}

        {report.isPending ? (
          <p className="text-muted-foreground text-sm">Working it out…</p>
        ) : !data ? null : data.rows.length === 0 ? (
          <EmptyState title="Nothing to report">
            {name === 'unarchived'
              ? 'Which is the right answer: verification cannot pass without an archive link (D-22).'
              : `No rows for ${session.year}.`}
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                {data.columns.map((column) => (
                  <Th key={column.key} numeric={isNumeric(column)}>
                    <button
                      type="button"
                      className="font-medium underline-offset-4 hover:underline"
                      onClick={() =>
                        setSort((current) =>
                          current?.key === column.key
                            ? { key: column.key, direction: current.direction === 1 ? -1 : 1 }
                            : { key: column.key, direction: 1 },
                        )
                      }
                    >
                      {column.label}
                      {sort?.key === column.key ? (sort.direction === 1 ? ' ↑' : ' ↓') : ''}
                    </button>
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  {data.columns.map((column) => (
                    <Td key={column.key} numeric={isNumeric(column)}>
                      <Cell value={row[column.key]} column={column} />
                    </Td>
                  ))}
                </tr>
              ))}
            </tbody>
            {hasTotals(data) ? (
              <tfoot>
                <tr className="font-medium">
                  {data.columns.map((column, index) => (
                    <Td key={column.key} numeric={isNumeric(column)}>
                      {index === 0 ? (
                        'Total'
                      ) : column.type === 'money' ? (
                        <Money cents={total(rows, column.key)} />
                      ) : column.type === 'number' && column.key !== 'participation_pct' ? (
                        total(rows, column.key)
                      ) : null}
                    </Td>
                  ))}
                </tr>
              </tfoot>
            ) : null}
          </Table>
        )}
      </Card>
    </>
  );
}

function Cell({ value, column }: { value: unknown; column: ReportColumn }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  switch (column.type) {
    case 'money':
      return <Money cents={Number(value)} />;
    case 'date':
      return <span className="whitespace-nowrap">{formatDate(String(value))}</span>;
    case 'instant':
      return <span className="whitespace-nowrap text-xs">{formatInstant(String(value))}</span>;
    case 'boolean':
      return value ? (
        <Badge tone="warn">yes</Badge>
      ) : (
        <span className="text-muted-foreground">no</span>
      );
    case 'number':
      return <>{Number(value)}</>;
    default:
      return <span className="text-sm">{String(value)}</span>;
  }
}

const isNumeric = (column: ReportColumn): boolean =>
  column.type === 'money' || column.type === 'number';

/** Totals only where a sum means something — a column of days waiting does not. */
const hasTotals = (data: ReportData): boolean =>
  data.columns.some((column) => column.type === 'money');

const total = (rows: ReadonlyArray<Record<string, unknown>>, key: string): number =>
  rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);

function compare(a: unknown, b: unknown, column: ReportColumn | undefined): number {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (column && isNumeric(column)) return Number(a) - Number(b);
  return String(a).localeCompare(String(b));
}
